import LegalLayout, { P, UL, H3 } from "@/components/site/LegalLayout";
import SITE from "@/lib/site";

export default function TermsOfService() {
  const L = SITE.legal;

  const sections = [
    {
      id: "acceptance",
      title: "1. Acceptance of Terms",
      body: (
        <>
          <P>
            These Terms of Service (“Terms”) form a binding legal agreement between you
            (“you,” “your,” or “User”) and {L.companyName} (“Ambuly,” “we,” “us,” or
            “our”). They govern your access to and use of the Ambuly website, mobile
            applications, and related services (collectively, the “Service”).
          </P>
          <P>
            By creating an account, booking a ride, or otherwise accessing the Service,
            you acknowledge that you have read, understood, and agree to be bound by
            these Terms and our Privacy Policy. If you do not agree, do not use the
            Service.
          </P>
        </>
      ),
    },
    {
      id: "service-description",
      title: "2. Description of the Service",
      body: (
        <>
          <P>
            Ambuly is a technology platform that connects patients, families, hospitals,
            and other requesters with independent third-party ambulance operators,
            drivers, and paramedics (“Operators”). Ambuly facilitates bookings,
            dispatch, navigation, in-trip communication, billing, and customer support.
          </P>
          <P>
            <strong className="text-[#14241F]">Important:</strong> Ambuly itself is not a
            medical provider and does not provide emergency medical services, medical
            advice, diagnosis, or treatment. Medical services are provided by
            independent, licensed Operators. In a life-threatening emergency, call your
            local emergency number immediately.
          </P>
        </>
      ),
    },
    {
      id: "eligibility",
      title: "3. Eligibility",
      body: (
        <>
          <P>To use the Service you must:</P>
          <UL
            items={[
              "Be at least 18 years of age, or the age of majority in your jurisdiction.",
              "Have the legal capacity to enter into a binding contract.",
              "Not be prohibited from receiving services under applicable law.",
              "Provide accurate, complete, and current information during registration and booking.",
            ]}
          />
          <P>
            Minors may use the Service only under the supervision and consent of a
            parent or legal guardian.
          </P>
        </>
      ),
    },
    {
      id: "accounts",
      title: "4. Accounts & Security",
      body: (
        <>
          <P>
            You are responsible for maintaining the confidentiality of your login
            credentials and for all activity that occurs under your account. You must
            notify us immediately at{" "}
            <a className="underline hover:text-[#1A3A32]" href={`mailto:${L.email}`}>{L.email}</a>{" "}
            of any unauthorized use or security breach.
          </P>
          <P>
            We may suspend or terminate accounts that violate these Terms, provide
            misleading information, or appear to be involved in fraud or abuse.
          </P>
        </>
      ),
    },
    {
      id: "bookings",
      title: "5. Bookings, Dispatch & Medical Care",
      body: (
        <>
          <H3>5.1 Booking requests</H3>
          <P>
            When you submit a booking request, you authorise Ambuly to forward your
            request and relevant information to Operators for dispatch. Estimated
            arrival times, vehicle types, and prices shown in the Service are estimates
            and may change based on real-world conditions.
          </P>
          <H3>5.2 Clinical decisions</H3>
          <P>
            All clinical decisions — including triage, treatment, hospital selection,
            medication, and refusal to transport — are made by licensed medical
            professionals in accordance with applicable laws and their professional
            judgment. Ambuly does not override or interfere with clinical decisions.
          </P>
          <H3>5.3 Cancellations & no-shows</H3>
          <UL
            items={[
              "You may cancel a scheduled booking; cancellation windows and fees are shown at the time of booking.",
              "No-show, late cancellations, and diversion requests may incur fees to cover the Operator's time, fuel and equipment.",
              "Emergency bookings already in progress cannot be cancelled once the ambulance is en route with a critical patient.",
            ]}
          />
        </>
      ),
    },
    {
      id: "fees",
      title: "6. Fees, Payment & Refunds",
      body: (
        <>
          <P>
            You agree to pay all fees associated with your use of the Service,
            including ride fares, subscription charges, taxes, and applicable third-
            party charges (such as toll, parking, or waiting time).
          </P>
          <UL
            items={[
              "Ambuly may present an estimated fare before booking; the final fare reflects actual distance, time, service level, and any additional services.",
              "Payments are processed through third-party payment providers; you authorise charges to your payment method on file.",
              "Refunds are governed by our Refund Policy and issued at our discretion, except where required by law.",
              "Late payments may incur interest and collection costs to the extent permitted by law.",
            ]}
          />
        </>
      ),
    },
    {
      id: "use-restrictions",
      title: "7. Acceptable Use & Restrictions",
      body: (
        <>
          <P>You agree not to:</P>
          <UL
            items={[
              "Use the Service for any unlawful, fraudulent, harmful, or abusive purpose.",
              "Submit false, misleading, or incomplete information, or impersonate any person.",
              "Interfere with, disrupt, or compromise the integrity or security of the Service.",
              "Reverse-engineer, decompile, or attempt to extract source code, except as permitted by law.",
              "Use the Service to harass, threaten or harm Operators, paramedics, dispatchers or other users.",
              "Introduce viruses, malware, scraping bots, or automated systems that place unreasonable load on our infrastructure.",
              "Resell, sublicense, or commercially exploit the Service without our prior written consent.",
            ]}
          />
        </>
      ),
    },
    {
      id: "third-party",
      title: "8. Third-Party Operators & Services",
      body: (
        <>
          <P>
            Operators providing ambulance services through the Service are independent
            contractors. Ambuly does not employ Operators or paramedics and is not
            responsible for their acts, omissions, medical decisions, equipment, or
            compliance with law, except to the extent required by applicable law.
          </P>
          <P>
            The Service may integrate with third-party platforms (payment processors,
            mapping providers, insurance networks). Your use of those services is
            governed by their respective terms and privacy policies.
          </P>
        </>
      ),
    },
    {
      id: "ip",
      title: "9. Intellectual Property",
      body: (
        <>
          <P>
            The Service, including all software, text, graphics, logos, images, and
            content (excluding User Content), is owned by or licensed to Ambuly and is
            protected by copyright, trademark, and other intellectual property laws. We
            grant you a limited, revocable, non-exclusive, non-transferable licence to
            access and use the Service for personal, non-commercial purposes in
            accordance with these Terms.
          </P>
          <P>
            “Ambuly” and the Ambuly logo are trademarks of {L.companyName}. You may not
            use them without our prior written consent.
          </P>
        </>
      ),
    },
    {
      id: "user-content",
      title: "10. User Content & Feedback",
      body: (
        <>
          <P>
            You retain ownership of content you submit through the Service, including
            bookings, notes, messages, ratings and reviews (“User Content”). By
            submitting User Content, you grant Ambuly a worldwide, non-exclusive,
            royalty-free licence to use, reproduce, store, modify, publish and display
            such content solely to operate and improve the Service.
          </P>
          <P>
            You are responsible for your User Content and represent that you have the
            rights necessary to grant the above licence. We may remove content that
            violates these Terms or applicable law.
          </P>
        </>
      ),
    },
    {
      id: "privacy",
      title: "11. Privacy",
      body: (
        <>
          <P>
            Your use of the Service is subject to our{" "}
            <a className="underline hover:text-[#1A3A32]" href="/privacy-policy">Privacy Policy</a>,
            which explains how we collect, use, and protect your information. By using
            the Service, you consent to the practices described in that Policy.
          </P>
        </>
      ),
    },
    {
      id: "disclaimers",
      title: "12. Disclaimers",
      body: (
        <>
          <P className="uppercase text-sm tracking-wider font-semibold text-[#14241F]">
            The Service is provided “as is” and “as available.”
          </P>
          <P>
            To the maximum extent permitted by law, Ambuly and its affiliates,
            officers, employees, agents, and licensors disclaim all warranties of any
            kind, whether express, implied, or statutory, including warranties of
            merchantability, fitness for a particular purpose, non-infringement, and
            any warranties arising from course of dealing or usage of trade.
          </P>
          <P>
            Ambuly does not warrant that the Service will be uninterrupted, error-free,
            secure, or that any specific ambulance will arrive within a specific time.
            Travel times depend on distance, traffic, weather, and other factors beyond
            our control.
          </P>
          <P>
            Ambuly is not a substitute for emergency medical services. In a life-
            threatening emergency, you must call your local emergency number.
          </P>
        </>
      ),
    },
    {
      id: "liability",
      title: "13. Limitation of Liability",
      body: (
        <>
          <P>
            To the maximum extent permitted by law, Ambuly and its affiliates shall not
            be liable for any indirect, incidental, special, consequential, punitive or
            exemplary damages, including loss of profits, revenue, data, goodwill, or
            other intangible losses, arising out of or relating to your use of (or
            inability to use) the Service, even if advised of the possibility of such
            damages.
          </P>
          <P>
            To the maximum extent permitted by law, our total aggregate liability for
            all claims arising out of or relating to these Terms or the Service shall
            not exceed the greater of: (a) the total amount paid by you to Ambuly in
            the six (6) months preceding the event giving rise to the claim; or (b)
            one hundred U.S. dollars (US$100).
          </P>
          <P>
            Nothing in these Terms excludes or limits our liability for fraud, gross
            negligence, wilful misconduct, death, or personal injury caused by our
            negligence where such limitation is not permitted by law.
          </P>
        </>
      ),
    },
    {
      id: "indemnification",
      title: "14. Indemnification",
      body: (
        <>
          <P>
            You agree to defend, indemnify, and hold harmless Ambuly and its affiliates
            from and against any claims, damages, obligations, losses, liabilities,
            costs, and expenses (including reasonable attorneys' fees) arising from:
            (i) your use of the Service; (ii) your violation of these Terms; (iii) your
            violation of any third-party right; or (iv) your User Content.
          </P>
        </>
      ),
    },
    {
      id: "termination",
      title: "15. Suspension & Termination",
      body: (
        <>
          <P>
            We may suspend or terminate your access to the Service at any time, with or
            without notice, if we believe you have violated these Terms, engaged in
            fraud or unsafe conduct, or where required by law. You may terminate your
            account at any time by following the instructions in the app or contacting
            support.
          </P>
          <P>
            Upon termination, the rights granted to you under these Terms will cease
            immediately. Sections intended to survive termination — including fees
            owed, intellectual property, disclaimers, limitation of liability,
            indemnification, and dispute resolution — will survive.
          </P>
        </>
      ),
    },
    {
      id: "governing-law",
      title: "16. Governing Law & Dispute Resolution",
      body: (
        <>
          <P>
            These Terms are governed by and construed in accordance with the laws of{" "}
            {L.jurisdiction}, without regard to conflict-of-laws principles. Subject to
            the arbitration provisions below, courts located in {L.jurisdiction} shall
            have exclusive jurisdiction over any disputes.
          </P>
          <H3>Informal resolution</H3>
          <P>
            Before filing any formal claim, you agree to contact us at{" "}
            <a className="underline hover:text-[#1A3A32]" href={`mailto:${L.email}`}>{L.email}</a>{" "}
            and attempt to resolve the matter informally for at least sixty (60) days.
          </P>
          <H3>Arbitration (where permitted)</H3>
          <P>
            To the extent permitted by law, any dispute not resolved informally shall
            be settled by binding individual arbitration under the rules of a
            recognised arbitration body in {L.jurisdiction}. You and Ambuly each waive
            the right to a trial by jury and the right to participate in a class action.
            Nothing in this Section prevents either party from seeking injunctive
            relief in a court of competent jurisdiction.
          </P>
        </>
      ),
    },
    {
      id: "app-stores",
      title: "17. App Store Additional Terms (Apple / Google)",
      body: (
        <>
          <H3>17.1 Apple App Store</H3>
          <UL
            items={[
              "These Terms are between you and Ambuly; Apple is not a party. Apple has no obligation to provide support or maintenance for the Service.",
              "If the app fails to conform to any applicable warranty, you may notify Apple, which will refund the purchase price (if any). To the maximum extent permitted by law, Apple has no other warranty obligation.",
              "Apple is not responsible for addressing any claims relating to the app or your use of it, including product liability, legal/regulatory compliance, or consumer protection claims.",
              "In the event of a third-party claim that the app infringes intellectual property rights, Ambuly (not Apple) is solely responsible for investigation, defence, settlement, and discharge.",
              "You represent that you are not located in an embargoed country and are not on a U.S. Government list of prohibited or restricted parties.",
              "Apple and its subsidiaries are third-party beneficiaries of these Terms and may enforce them against you.",
            ]}
          />
          <H3>17.2 Google Play</H3>
          <UL
            items={[
              "Your use of the app is also subject to the Google Play Terms of Service.",
              "Google is not responsible for the app or its contents, or for providing support, maintenance, refunds, or warranty services for the app.",
              "To the maximum extent permitted by law, Google shall not be liable for any claims, damages, or losses arising from your use of the app.",
            ]}
          />
        </>
      ),
    },
    {
      id: "export",
      title: "18. Export Controls & Sanctions",
      body: (
        <>
          <P>
            You agree to comply with all applicable export-control, sanctions, and
            anti-corruption laws. You represent and warrant that you are not located in
            a country subject to embargo and are not listed on any government sanctions
            or restricted-parties list.
          </P>
        </>
      ),
    },
    {
      id: "changes",
      title: "19. Changes to These Terms",
      body: (
        <>
          <P>
            We may modify these Terms from time to time. When we make material changes,
            we will provide reasonable notice before they become effective, such as by
            updating the “Last updated” date and posting in the Service or via email.
            Your continued use of the Service after the effective date constitutes
            acceptance of the revised Terms.
          </P>
        </>
      ),
    },
    {
      id: "misc",
      title: "20. Miscellaneous",
      body: (
        <>
          <UL
            items={[
              "Severability: If any provision is found unenforceable, the remaining provisions will remain in full force and effect.",
              "No waiver: Failure to enforce any right or provision does not constitute a waiver.",
              "Assignment: You may not assign these Terms without our prior written consent; we may assign them without restriction.",
              "Entire agreement: These Terms, together with the Privacy Policy and any order documents, constitute the entire agreement between you and Ambuly.",
              "Force majeure: Neither party is liable for delays caused by events beyond its reasonable control.",
              "Notices: Notices to you may be delivered via the Service or to the contact details on your account. Notices to Ambuly should be sent to the contact details below.",
            ]}
          />
        </>
      ),
    },
    {
      id: "contact",
      title: "21. Contact Us",
      body: (
        <>
          <P>For questions about these Terms, please contact us at:</P>
          <div className="rounded-2xl bg-[#EDF1ED] p-6 mt-3">
            <p className="text-[#14241F]"><strong>{L.companyName}</strong></p>
            <p className="text-[#495D56] mt-1">{L.address}</p>
            <p className="text-[#495D56] mt-1">Email: <a className="underline hover:text-[#1A3A32]" href={`mailto:${L.email}`}>{L.email}</a></p>
            <p className="text-[#495D56] mt-1">Jurisdiction: {L.jurisdiction}</p>
          </div>
        </>
      ),
    },
  ];

  return (
    <div data-testid="terms-of-service-page">
      <LegalLayout
        eyebrow="Legal"
        title="Terms of Service"
        lastUpdated={L.effectiveDate}
        sections={sections}
      >
        <p className="text-[#495D56] text-[17px] leading-relaxed">
          Please read these Terms carefully before using Ambuly. They define the rules,
          rights and responsibilities that apply to everyone who uses our website,
          mobile applications and services, and include important provisions about
          medical disclaimers, payments, liability and dispute resolution.
        </p>
      </LegalLayout>
    </div>
  );
}
